import { cn } from '@commenda/utils';
import React, { useEffect, useState } from 'react';
import { InputErrorMessage } from '../InputErrorMessage';

// Helper function to format numbers with commas
const formatNumberWithCommas = (
  value: number | null,
  numberLocaleFormat = 'en-US'
): string => {
  if (value === null) return '';

  // Split the number into integer and decimal parts
  const [integerPart, decimalPart] = value.toString().split('.');

  // Format the integer part with commas
  const formattedInteger =
    Number(integerPart).toLocaleString(numberLocaleFormat);

  // If there's a decimal part, append it to the formatted integer
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

// Helper function to parse number input with commas
const parseFormattedNumber = (value: string): number | null => {
  if (!value) return null;
  // Remove all commas except decimal point
  const parsedValue = value.replace(/,/g, '');
  return Number.isNaN(Number(parsedValue)) ? null : Number(parsedValue);
};

type ElementRenderProps = {
  className: string;
  [key: string]: any;
};

export type NumberInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  numberLocaleFormat?: string; // like toLocaleString('en-US');
  formatWithCommas?: boolean; // Whether to format numbers with commas (default: true)
  error?: string;
  showErrorMessage?: boolean;
  leftEl?: (props: ElementRenderProps) => React.ReactNode;
  rightEl?: (props: ElementRenderProps) => React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  type?: string;
  value?: string | number | readonly string[] | undefined;
  onChange?: (
    number: number | null,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
};

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      className,
      error,
      leftEl,
      rightEl,
      showErrorMessage = true,
      fullWidth = false,
      type = 'text',
      value,
      numberLocaleFormat = 'en-US',
      formatWithCommas = false,
      onChange,
      ...props
    },
    ref
  ) => {
    const baseElementClass =
      'absolute top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5';
    const leftElClass = cn(baseElementClass, 'left-3');
    const rightElClass = cn(baseElementClass, 'right-3');

    const [displayValue, setDisplayValue] = useState<string>('');

    // Format the value with commas when it changes
    useEffect(() => {
      if (value !== undefined) {
        const numValue =
          typeof value === 'string'
            ? parseFormattedNumber(value)
            : (value as number);
        if (formatWithCommas) {
          setDisplayValue(formatNumberWithCommas(numValue, numberLocaleFormat));
        } else {
          setDisplayValue(numValue !== null ? String(numValue) : '');
        }
      } else {
        setDisplayValue('');
      }
    }, [value, formatWithCommas, numberLocaleFormat]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      const numericValue = parseFormattedNumber(inputValue);
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: numericValue !== null ? String(numericValue) : '',
        },
      };

      // allow empty string but not non-numeric strings
      if (numericValue === null && inputValue !== '') {
        return;
      }

      // only numbers are allowed
      setDisplayValue(inputValue);
      // Pass the parsed numeric value to the original onChange handler
      if (onChange) {
        onChange(
          numericValue,
          syntheticEvent as React.ChangeEvent<HTMLInputElement>
        );
      }
    };

    return (
      <div className="w-full flex flex-col gap-2">
        <div className={cn('relative', fullWidth ? 'w-full' : '')}>
          {leftEl && (
            <div className={leftElClass}>
              {leftEl({ className: 'w-full h-full' })}
            </div>
          )}
          <input
            type={type}
            className={cn(
              'flex w-full box-border txt-small-regular rounded-sm px-xl py-2.5 bg-bg-primary text-text-primary txt-medium-regular placeholder:text-text-placeholder ring-0 h-10 transition-colors',
              'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled disabled:border-border-disabled disabled:pointer-events-none hide-date-icon appearance-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)]',
              leftEl && 'pl-10',
              rightEl && 'pr-9',
              'border',
              error
                ? 'border-border-error hover:border-border-error-hover focus-visible:border-border-error-hover'
                : 'border-border-primary hover:border-border-primary-hover focus-visible:border-fg-brand',
              className
            )}
            ref={ref}
            value={displayValue}
            onChange={handleInputChange}
            {...props}
          />
          {rightEl && (
            <div className={rightElClass}>
              {rightEl({ className: 'w-full h-full' })}
            </div>
          )}
        </div>
        {error && showErrorMessage ? (
          <InputErrorMessage message={error} />
        ) : null}
      </div>
    );
  }
);

NumberInput.displayName = 'NumberInput';

export { NumberInput };
